import { useState } from "react";
import { ArrowRight, CheckCircle2, Phone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MARKETS = [
  {
    name: "Government & Military",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/tmwx4k6g_Government%20%26%20Military.png",
    desc: "Fresh, reliable sushi programs for military bases and government facilities — serving service members and their families daily.",
  },
  {
    name: "Airports & Transportation",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/glvcpgst_Airport%20%26%20Transportation.png",
    desc: "Compact, high-throughput formats for concourses, terminals and transit hubs — built for speed and freshness.",
  },
  {
    name: "Supermarkets",
    image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=80",
    desc: "Branded chef stations, live rolling theater, and seamless POS integration inside your grocery footprint.",
  },
  {
    name: "Corporate Offices",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/gbwin7dy_Sandar%20Sushi%20Store.PNG",
    desc: "Specialty stations, pop-ups and smart-cooler programs for workplaces and mixed-use campuses.",
  },
  {
    name: "Universities & Campuses",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/kk99rob7_Universities%20%26%20Campuses.png",
    desc: "Meal-plan compatible kiosks and dining-hall concepts designed around how students actually eat.",
  },
  {
    name: "Healthcare Facilities",
    image: "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?auto=format&fit=crop&w=1200&q=80",
    desc: "High-hygiene packaging, sodium-conscious options, and round-the-clock refrigerated freshness for care teams.",
  },
];

const initForm = { name: "", email: "", phone: "", company: "", message: "" };

function ContactDialog({ open, onOpenChange, prefillMarket }) {
  const [form, setForm] = useState(initForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", {
        ...form,
        message: prefillMarket ? `[${prefillMarket}] ${form.message}` : form.message,
      });
      setSent(true);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm(initForm);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send — try calling 718-216-2013");
    } finally { setSending(false); }
  };

  const close = () => { onOpenChange(false); setTimeout(() => setSent(false), 300); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {sent ? "Thanks!" : (prefillMarket ? `Bring Sandar Sushi to ${prefillMarket}` : "Contact Sandar Sushi")}
          </DialogTitle>
        </DialogHeader>
        {sent ? (
          <div className="text-center py-6" data-testid="contact-thankyou">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4"/>
            <p className="text-neutral-600">We received your note. Our partnerships team will reach out within one business day.</p>
            <div className="mt-6 flex justify-center gap-4 text-sm">
              <a href="tel:7182162013" className="inline-flex items-center gap-1.5 text-neutral-700 hover:text-[hsl(var(--primary))]"><Phone className="w-4 h-4"/>718-216-2013</a>
              <a href="mailto:info@sandarsushi.com" className="inline-flex items-center gap-1.5 text-neutral-700 hover:text-[hsl(var(--primary))]"><Mail className="w-4 h-4"/>info@sandarsushi.com</a>
            </div>
            <Button onClick={close} variant="outline" className="mt-6 rounded-full">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="contact-name"/></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="contact-phone-input"/></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="contact-email"/></div>
            <div><Label>Company / Location</Label><Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Airport, base, supermarket…" data-testid="contact-company"/></div>
            <div><Label>How can we help?</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required data-testid="contact-message"/></div>
            <Button type="submit" disabled={sending} className="w-full h-11 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="contact-submit">
              {sending ? "Sending…" : "Send Message"}
            </Button>
            <p className="text-xs text-center text-neutral-400">Prefer to call? Reach us at <a href="tel:7182162013" className="underline">718-216-2013</a></p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Markets() {
  const [contactOpen, setContactOpen] = useState(false);
  const [prefillMarket, setPrefillMarket] = useState("");

  const openContact = (market) => { setPrefillMarket(market || ""); setContactOpen(true); };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <p className="eyebrow mb-2">Markets We Serve</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="markets-title">Wherever people gather — we roll.</h1>
        <p className="mt-4 text-neutral-600 max-w-2xl leading-relaxed">From military installations to airports and supermarkets, Sandar Sushi builds custom sushi programs tuned to each location's rhythm, footprint, and guests.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {MARKETS.map((m) => (
            <article key={m.name} data-testid={`market-${m.name}`}>
              <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-5 bg-neutral-100">
                <img src={m.image} alt={m.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
              </div>
              <h3 className="font-display font-semibold text-2xl tracking-tight">{m.name}</h3>
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{m.desc}</p>
              <button
                type="button"
                onClick={() => openContact(m.name)}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))] hover:gap-2 transition-all"
                data-testid={`market-learn-${m.name}`}
              >
                Learn More <ArrowRight className="w-3.5 h-3.5"/>
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="eyebrow mb-3">Partner with us</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Bring Sandar Sushi to your space.</h2>
            <p className="mt-3 text-neutral-400 max-w-xl">Tell us about your location and traffic — we'll design the right sushi program for you.</p>
          </div>
          <button
            type="button"
            onClick={() => openContact("")}
            className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 h-12 px-8 font-semibold transition-colors"
            data-testid="markets-contact"
          >
            Contact Us <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </section>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} prefillMarket={prefillMarket}/>
    </div>
  );
}
